<!--
  WorkflowTrackPanel — 50" TV-optimised redesign.
  12-month grid: months 1-6 (top row) · 7-12 (bottom row).
  Each card shows status mark, emission total, scope bar, YoY delta (전년 동월).
-->
<template>
    <div class="wf-wrap">
        <!-- ── Header ─────────────────────────────────────────────── -->
        <div class="wf-head">
            <div class="wf-head-info">
                <span class="wf-eyebrow">산정 워크플로</span>
            </div>
            <div class="wf-legend-flow" aria-label="월별 단계 상태 흐름">
                <span v-for="(s, idx) in flowLegend" :key="s.status" class="wf-legend-flow-group">
                    <span v-if="idx > 0" class="wf-legend-sep" aria-hidden="true"></span>
                    <span :class="['wf-legend-step', 'wf-legend-step--' + s.status]" :title="s.label + ' · ' + s.hint">
                        <span :class="['wf-legend-mark', 'wf-legend-mark--' + s.status]"></span>
                        <span class="wf-legend-step-text">
                            <span class="wf-legend-step-name">{{ s.label }}</span>
                            <!-- <span class="wf-legend-step-hint">{{ s.hint }}</span> -->
                        </span>
                        <!-- <span v-if="s.count > 0" class="wf-legend-cnt num">{{ s.count }}</span> -->
                    </span>
                </span>
            </div>
        </div>

        <!-- ── 12-month cards ─────────────────────────────────────── -->
        <div class="wf-grid">
            <div v-for="m in normalizedMonths" :key="m.month" :class="['wf-card', 'wf-card--' + m.status]">
                <!-- ① Header: month + status chip -->
                <div class="wf-card-hd">
                    <span class="wf-card-mo">
                        <span class="wf-card-mo-label">{{ m.month }}<em class="wf-wol">월</em></span>
                        <i
                            v-if="m.isCheckPoint"
                            class="fas fa-flag wf-checkpoint-flag"
                            aria-hidden="true"
                            title="체크포인트"
                        ></i>
                    </span>
                    <span class="wf-chip">{{ statusLabel(m.status) }}</span>
                </div>

                <!-- ② Body: hero emission OR compact status line -->
                <div class="wf-card-bd">
                    <template v-if="m.totalEmissions > 0">
                        <div class="wf-hero">
                            <span class="wf-em-val num">{{ formatEmission(m.totalEmissions) }}</span>
                            <span class="wf-em-unit">{{ unitShort }}</span>
                        </div>
                        <div v-if="m.yoyDelta !== null" :class="['wf-delta num', yoyClass(m.yoyDelta)]">
                            <span class="wf-delta-lbl">전년 대비</span>

                            <span class="wf-delta-arrow">{{ yoyDir(m.yoyDelta) }}</span>
                            {{ Math.abs(m.yoyDelta).toFixed(1) }}%
                        </div>
                        <span v-else class="wf-delta wf-delta--none">전년 데이터 없음</span>
                    </template>
                    <template v-else>
                        <div :class="['wf-empty-state', 'wf-empty-state--' + m.status]">
                            <span class="wf-empty-text">{{ statusHint(m.status) }}</span>
                        </div>
                    </template>
                </div>

                <!-- ③ Footer: scope bar (data cards only) -->
                <div class="wf-card-ft">
                    <div v-if="m.totalEmissions > 0" class="wf-sbar">
                        <div class="wf-sbar-s1" :style="{ width: m.s1Pct + '%' }"></div>
                        <div class="wf-sbar-s2" :style="{ width: m.s2Pct + '%' }"></div>
                        <div v-if="m.s3Pct > 0" class="wf-sbar-s3" :style="{ width: m.s3Pct + '%' }"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatEmissionValue, toDisplayValue } from "@/submodules/carbon-plugin/utils/formatters";

function statusOf(item) {
    if (item.collStat === "COMPLETED" && (item.calcStat === "CALCULATED" || item.calcStat === "VERIFIED"))
        return "done";
    if (item.collStat === "COLLECTING") return "active";
    if (item.collStat === "COMPLETED" && item.calcStat === "PENDING") return "calcPending";
    return "pending";
}

export default {
    name: "WorkflowTrackPanel",
    props: {
        months: { type: Array, default: () => [] },
        /** 전년 overview widgets.inventory.months (동일 month 키로 YoY) */
        prevYearMonths: { type: Array, default: () => [] },
        apiUnit: { type: String, default: "kgCO2e" },
        displayUnit: { type: String, default: "kgCO2e", validator: (v) => ["kgCO2e", "tCO2e"].includes(v) },
    },
    computed: {
        unitShort() {
            return this.displayUnit === "tCO2e" ? "t" : "kg";
        },
        prevYearByMonth() {
            const map = new Map();
            for (const it of this.prevYearMonths) {
                const month = it.month ?? (it.period ? parseInt(String(it.period).slice(4, 6), 10) : null);
                if (!month) continue;
                const total = Number(it.totalEmissions ?? it.total ?? 0);
                map.set(month, total);
            }
            return map;
        },
        normalizedMonths() {
            const map = new Map();
            for (const it of this.months) {
                const month = it.month ?? (it.period ? parseInt(String(it.period).slice(4, 6), 10) : null);
                if (!month) continue;
                const total = it.totalEmissions ?? it.total ?? 0;
                const s1 = it.scope1Emissions ?? it.scope1 ?? 0;
                const s2 = it.scope2Emissions ?? it.scope2 ?? 0;
                const s3 = it.scope3Emissions ?? it.scope3 ?? 0;
                map.set(month, {
                    ...it,
                    month,
                    isCheckPoint: it.isCheckPoint ?? month % 3 === 0,
                    status: statusOf(it),
                    totalEmissions: total,
                    s1Pct: total > 0 ? (s1 / total) * 100 : 0,
                    s2Pct: total > 0 ? (s2 / total) * 100 : 0,
                    s3Pct: total > 0 ? (s3 / total) * 100 : 0,
                    yoyDelta: null,
                });
            }
            const arr = Array.from({ length: 12 }, (_, i) => {
                const month = i + 1;
                const m = map.get(month);
                if (m) return m;
                return {
                    month,
                    status: "pending",
                    isCheckPoint: month % 3 === 0,
                    totalEmissions: 0,
                    s1Pct: 0,
                    s2Pct: 0,
                    s3Pct: 0,
                    yoyDelta: null,
                };
            });
            for (const entry of arr) {
                const prevYearTotal = this.prevYearByMonth.get(entry.month);
                const curr = entry.totalEmissions;
                if (prevYearTotal > 0 && curr > 0) {
                    entry.yoyDelta = ((curr - prevYearTotal) / prevYearTotal) * 100;
                }
            }
            return arr;
        },
        counts() {
            const c = { done: 0, active: 0, calcPending: 0, pending: 0 };
            for (const m of this.normalizedMonths) c[m.status]++;
            return c;
        },
        /** CalcWorkflowMain 마커 설명 순서: 대기 → 수집중 → 산정대기 → 완료 */
        flowLegend() {
            const c = this.counts;
            return [
                { status: "pending", label: "대기", hint: "수집 대기", count: c.pending },
                { status: "active", label: "수집 중", hint: "자료 수집", count: c.active },
                { status: "calcPending", label: "산정 대기", hint: "수집 완료", count: c.calcPending },
                { status: "done", label: "완료", hint: "산정·검증", count: c.done },
            ];
        },
    },
    methods: {
        statusLabel(s) {
            return { done: "완료", active: "수집 중", calcPending: "산정 대기", pending: "대기" }[s] ?? s;
        },
        statusHint(s) {
            return (
                {
                    done: "산정 완료",
                    active: "자료 수집 중",
                    calcPending: "산정 대기",
                    pending: "수집 대기",
                }[s] ?? this.statusLabel(s)
            );
        },
        formatEmission(v) {
            const n = Number(v);
            if (v == null || v === "" || isNaN(n)) return "—";
            const displayed = toDisplayValue(n, this.apiUnit, this.displayUnit);
            // t 표시 등 1 미만은 정수(0자리)로 반올림되면 0으로 보임 → 시설/카테고리 패널과 동일
            const decimals = displayed > 0 && displayed < 1 ? 2 : 0;
            return formatEmissionValue(n, this.apiUnit, this.displayUnit, decimals);
        },
        yoyClass(v) {
            return v == null ? "" : v < 0 ? "wf-yoy--down" : "wf-yoy--up";
        },
        yoyDir(v) {
            return v < 0 ? "▼" : "▲";
        },
    },
};
</script>

<style scoped>
/* ── Wrapper ─────────────────────────────────────────────────────── */
.wf-wrap {
    --wf-active: #d97706;
    --wf-active-ink: #9a5a00;
    --wf-active-border: #f5d99a;
    --wf-active-chip: #d97706;
    --wf-calc: #6c5fba;
    --wf-calc-ink: #5b4fa3;
    --wf-calc-border: #dedaf0;
    --wf-calc-chip: #64748b;
    --wf-done: #0f766e;
    --wf-done-ink: #0f5f59;
    --wf-done-border: #bfe4df;
    --wf-done-chip: #0f766e;
    --wf-pending: #94a3b8;
    --wf-card-bg: #f8fafc;
    --wf-border: #e2e8f0;
    --wf-border-soft: #edf2f7;
    --wf-stripe: rgba(15, 23, 42, 0.045);
    background: #ffffff;
    border: 1px solid var(--wf-border);
    border-radius: 14px;
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: var(--dash-ink);
    height: 100%;
    min-height: 0;
    box-sizing: border-box;
}
.num {
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
}

/* ── Header ──────────────────────────────────────────────────────── */
.wf-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    gap: 16px;
}
.wf-head-info {
    display: flex;
    gap: 2px;
}
.wf-eyebrow {
    font-size: var(--dash-panel-eyebrow-size);
    letter-spacing: 0;
    text-transform: uppercase;
    color: var(--dash-ink-mute);
    font-weight: 600;
}
.wf-headline {
    display: flex;
    gap: 5px;
}
.wf-hl-num {
    font-size: 16px;
    font-weight: 800;
    color: var(--dash-accent);
    letter-spacing: 0;
    line-height: 1;
}
.wf-hl-den {
    font-size: 16px;
    font-weight: 500;
    color: var(--dash-ink-mute);
    letter-spacing: 0;
}
.wf-hl-txt {
    font-size: 12px;
    font-weight: 600;
    color: var(--dash-ink-soft);
}

/* Legend — 상태는 작은 표식과 라인으로만 구분 */
.wf-legend-flow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 5px;
}
.wf-legend-flow-group {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}
/* 그룹 간 전이 화살표 */
.wf-legend-sep {
    position: relative;
    width: 16px;
    height: 14px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.wf-legend-sep::before {
    content: "";
    position: absolute;
    left: 1px;
    right: 4px;
    height: 1px;
    background: linear-gradient(to right, var(--wf-border-soft), var(--wf-border));
}
.wf-legend-sep::after {
    content: "";
    position: absolute;
    right: 2px;
    top: 50%;
    width: 5px;
    height: 5px;
    border-top: 1.5px solid #b6c0cb;
    border-right: 1.5px solid #b6c0cb;
    transform: translateY(-50%) rotate(45deg);
}

.wf-legend-step {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 7px 3px 6px;
    border-radius: 999px;
    background: transparent;
    border: 1px solid var(--wf-border);
}
.wf-legend-step--active {
    border-color: var(--wf-active-border);
}
.wf-legend-step--calcPending {
    border-style: dashed;
    border-color: var(--wf-calc-border);
}
.wf-legend-step--done {
    border-color: var(--wf-done-border);
}
.wf-legend-step--pending {
    background: repeating-linear-gradient(-45deg, transparent 0 5px, var(--wf-stripe) 5px 6px), var(--wf-card-bg);
}

/* 마크 점 */
.wf-legend-mark {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: transparent;
    border: 1.5px solid currentColor;
}
.wf-legend-mark--active {
    color: var(--wf-active);
    background: currentColor;
}
.wf-legend-mark--calcPending {
    color: var(--wf-calc);
    border-style: dashed;
}
.wf-legend-mark--done {
    color: var(--wf-done);
    background: currentColor;
}
.wf-legend-mark--pending {
    color: var(--wf-pending);
}

/* 텍스트 */
.wf-legend-step-text {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
}
.wf-legend-step-name {
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
    color: var(--dash-ink-soft);
}
.wf-legend-step--active .wf-legend-step-name {
    color: var(--wf-active-ink);
}
.wf-legend-step--calcPending .wf-legend-step-name {
    color: var(--wf-calc-ink);
}
.wf-legend-step--done .wf-legend-step-name {
    color: var(--wf-done-ink);
}
.wf-legend-step--pending .wf-legend-step-name {
    color: var(--dash-ink-faint);
}

.wf-legend-step-hint {
    font-size: 8px;
    font-weight: 500;
    color: var(--dash-ink-mute);
    white-space: nowrap;
}

/* 개수 배지 */
.wf-legend-cnt {
    min-width: 14px;
    text-align: center;
    padding: 1px 4px;
    border-radius: 999px;
    font-size: 9px;
    font-weight: 800;
    line-height: 1.4;
    background: transparent;
    border: 1px solid currentColor;
    opacity: 0.8;
}
.wf-legend-step--active .wf-legend-cnt {
    color: var(--wf-active-ink);
}
.wf-legend-step--calcPending .wf-legend-cnt {
    color: var(--wf-calc-ink);
}
.wf-legend-step--done .wf-legend-cnt {
    color: var(--wf-done-ink);
}
.wf-legend-step--pending .wf-legend-cnt {
    color: var(--dash-ink-faint);
}

/* ── Month card grid ─────────────────────────────────────────────── */
.wf-grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 13px;
}

/* ── Base card ───────────────────────────────────────────────────── */
.wf-card {
    position: relative;
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 10px 12px 8px;
    border-radius: 8px;
    background: var(--wf-card-bg);
    overflow: hidden;
    min-height: 0;
    transition:
        transform 0.25s ease,
        border-color 0.25s ease,
        box-shadow 0.25s ease;

    box-shadow: rgba(0, 0, 0, 0.15) 0px 3px 3px 0px;
}

.wf-card--active {
    border-color: var(--wf-active-border);
}
.wf-card--calcPending {
    border-color: var(--wf-calc-border);
    border-style: dashed;
    border-width: 1.5px;
}
.wf-card--done {
    border-color: var(--wf-done-border);
}
.wf-card--pending {
    background: repeating-linear-gradient(-45deg, transparent 0 7px, var(--wf-stripe) 7px 8px), var(--wf-card-bg);
    border-color: var(--wf-border);
}

/* ── Quarter badge ───────────────────────────────────────────────── */
.wf-qbadge {
    position: absolute;
    top: 8px;
    right: 9px;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0;
    line-height: 1;
}
.wf-card--active .wf-qbadge {
    color: var(--wf-active);
}
.wf-card--calcPending .wf-qbadge {
    color: var(--wf-calc);
}
.wf-card--done .wf-qbadge {
    color: var(--wf-done);
}
.wf-card--pending .wf-qbadge {
    color: var(--dash-ink-faint);
}

/* ── ① Header: month + status chip ──────────────────────────────── */
.wf-card-hd {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
    padding-top: 2px;
}

.wf-card-mo {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
    color: var(--dash-ink);
}
.wf-checkpoint-flag {
    font-size: 16px;
    flex-shrink: 0;
    color: var(--dash-ink-faint);
    opacity: 0.7;
}
.wf-card--active .wf-card-mo {
    color: var(--dash-ink);
}
.wf-card--calcPending .wf-card-mo {
    color: var(--dash-ink);
}
.wf-card--done .wf-card-mo {
    color: var(--dash-ink);
}
.wf-card--pending .wf-card-mo {
    color: var(--dash-ink-mute);
}

.wf-card--active .wf-checkpoint-flag {
    color: var(--wf-active);
}
.wf-card--calcPending .wf-checkpoint-flag {
    color: var(--wf-calc);
}
.wf-card--done .wf-checkpoint-flag {
    color: var(--wf-done);
}
.wf-card--pending .wf-checkpoint-flag {
    color: var(--dash-ink-faint);
}

.wf-wol {
    font-style: normal;
    font-size: 11px;
    font-weight: 600;
    margin-left: 1px;
    opacity: 0.45;
}

.wf-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0;
    padding: 2px 8px 2px 7px;
    border-radius: 999px;
    border: 1px solid var(--wf-border);
    background: transparent;
    color: var(--dash-ink-soft);
    line-height: 1.5;
    white-space: nowrap;
    flex-shrink: 0;
    margin-top: 2px;
}
/* .wf-chip::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 999px;
    border: 1.5px solid currentColor;
    flex-shrink: 0;
} */
.wf-card--active .wf-chip {
    background: var(--wf-active-chip);
    border-color: var(--wf-active-chip);
    color: #ffffff;
    box-shadow: 0 4px 10px rgba(217, 119, 6, 0.22);
}
.wf-card--active .wf-chip::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #ffffff;
    border: 0;
    flex-shrink: 0;
    animation: wfChipGlow 1.35s ease-in-out infinite;
}
.wf-card--calcPending .wf-chip {
    background: var(--wf-calc-chip);
    border-color: var(--wf-calc-chip);
    border-style: solid;
    color: #ffffff;
    box-shadow: 0 4px 10px rgba(71, 85, 105, 0.18);
}
.wf-card--calcPending .wf-chip::before {
    content: none;
}
.wf-card--done .wf-chip {
    background: var(--wf-done-chip);
    border-color: var(--wf-done-chip);
    color: #ffffff;
    box-shadow: 0 4px 10px rgba(15, 118, 110, 0.18);
}
.wf-card--done .wf-chip::before {
    content: none;
}
.wf-card--pending .wf-chip {
    background: transparent;
    border-color: var(--wf-border);
    box-shadow: none;
    color: var(--dash-ink-faint);
}
.wf-card--pending .wf-chip::before {
    content: none;
}
@keyframes wfChipGlow {
    0%,
    100% {
        opacity: 0.55;
        box-shadow:
            0 0 0 0 rgba(255, 255, 255, 0.44),
            0 0 4px rgba(255, 255, 255, 0.62);
        transform: scale(0.92);
    }
    50% {
        opacity: 1;
        box-shadow:
            0 0 0 4px rgba(255, 255, 255, 0.18),
            0 0 10px rgba(255, 255, 255, 0.96);
        transform: scale(1.08);
    }
}

/* ── ② Body: 왼쪽 정렬, 자연스러운 읽기 흐름 ─────────────────────── */
.wf-card-bd {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 5px;
    min-height: 0;
}

/* Hero emission value */
.wf-hero {
    display: flex;
    align-items: baseline;
    gap: 3px;
    line-height: 1;
}
.wf-em-val {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1;
    color: #4b5560;
}
.wf-em-unit {
    font-size: 12px;
    font-weight: 500;
    color: var(--dash-ink-mute);
    opacity: 0.55;
    padding-bottom: 1px;
}
.wf-card--active .wf-em-val {
    color: #4b5560;
}
.wf-card--active .wf-em-unit {
    color: var(--dash-ink-mute);
}
.wf-card--calcPending .wf-em-val {
    color: var(--dash-ink);
}
.wf-card--calcPending .wf-em-unit {
    color: var(--dash-ink-mute);
}
.wf-card--done .wf-em-val {
    color: #4b5560;
}
.wf-card--done .wf-em-unit {
    color: var(--dash-ink-mute);
}

/* YoY delta */
.wf-delta {
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    border-radius: 999px;
    background: transparent;
}
.wf-card--active .wf-delta {
    border-color: var(--wf-active-border);
}
.wf-card--calcPending .wf-delta {
    border-color: var(--wf-calc-border);
}
.wf-card--done .wf-delta {
    border-color: var(--wf-done-border);
}

.wf-delta-arrow {
    font-size: 7px;
}
.wf-delta-lbl {
    font-size: 11px;
    color: #b6b6ad;
}
.wf-delta--none {
    font-size: 9px;
    font-weight: 400;
    opacity: 0.3;
    background: transparent !important;
    padding: 0;
}

/* YoY 방향 색상 */
.wf-yoy--down {
    color: oklch(0.42 0.12 168);
}
.wf-yoy--up {
    color: oklch(0.48 0.14 25);
}
.wf-card--done .wf-delta.wf-yoy--down {
    color: oklch(0.42 0.12 168);
}
.wf-card--done .wf-delta.wf-yoy--up {
    color: oklch(0.48 0.14 25);
}

/* No-data 상태도 작은 라인으로만 표시 */
.wf-empty-state {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 7px;
    align-self: stretch;
    color: var(--dash-ink-mute);
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}
.wf-empty-mark {
    width: 28px;
    height: 2px;
    border-radius: 999px;
    background: currentColor;
    opacity: 0.55;
}
.wf-empty-state--active {
    color: var(--wf-active-ink);
}
.wf-empty-state--calcPending {
    color: var(--wf-calc-ink);
}

.wf-empty-state--done {
    color: var(--wf-done-ink);
}
.wf-empty-state--pending {
    color: var(--dash-ink-faint);
}

/* ── ③ Footer: scope bar ─────────────────────────────────────────── */
.wf-card-ft {
    min-height: 5px;
    display: flex;
    align-items: flex-end;
}

.wf-sbar {
    width: 100%;
    height: 3px;
    border-radius: 999px;
    background: oklch(0 0 0 / 0.08);
    display: flex;
    overflow: hidden;
}
.wf-card--active .wf-sbar {
    background: oklch(0 0 0 / 0.08);
}
.wf-card--calcPending .wf-sbar {
    background: oklch(0 0 0 / 0.08);
}
.wf-card--done .wf-sbar {
    background: oklch(0 0 0 / 0.08);
}
.wf-card--pending .wf-sbar {
    background: var(--wf-border);
}

.wf-sbar-s1 {
    height: 100%;
    background: var(--dash-scope1);
    flex-shrink: 0;
}
.wf-sbar-s2 {
    height: 100%;
    background: var(--dash-scope2);
    flex-shrink: 0;
}
.wf-sbar-s3 {
    height: 100%;
    background: var(--dash-scope3);
    flex-shrink: 0;
}
.wf-card--done .wf-sbar-s1 {
    background: var(--dash-scope1);
}
.wf-card--done .wf-sbar-s2 {
    background: var(--dash-scope2);
}
.wf-card--active .wf-sbar-s1 {
    background: var(--dash-scope1);
}
.wf-card--active .wf-sbar-s2 {
    background: var(--dash-scope2);
}
</style>
